import type {TriggerIntegration} from '@cohort/merchants/apps';
import {getTriggerIntegrations} from '@cohort/merchants/apps';
import type {TriggerIntegrationStruct} from '@cohort/shared/schema/apps/app';
import {useCallback} from 'react';
import {useTranslation} from 'react-i18next';

export type Output<T extends TriggerIntegrationStruct> = {
  getTriggerIntegration: (triggerIntegrationId: T['Id']) => TriggerIntegration<T>;
  getTriggerIntegrationTitle: (integration: TriggerIntegration<T>) => string;
};

export const useTriggerIntegrations = <T extends TriggerIntegrationStruct>(): Output<T> => {
  const {t} = useTranslation();

  const getTriggerIntegration = useCallback(
    (triggerIntegrationId: T['Id']): TriggerIntegration<T> => {
      const triggersIntegrations = getTriggerIntegrations();
      const triggerIntegration = triggersIntegrations.find(
        integration => integration.spec.id === triggerIntegrationId
      );

      if (!triggerIntegration) {
        throw new Error(
          `No TriggerIntegration found for triggerIntegrationId: ${triggerIntegrationId}`
        );
      }
      return triggerIntegration as unknown as TriggerIntegration<T>;
    },
    []
  );

  const getTriggerIntegrationTitle = (integration: TriggerIntegration<T>): string => {
    const [appId, integrationId] = integration.spec.id.split('.');

    return t(`triggerIntegrations.${integrationId}.title`, {
      ns: `app-${appId}`,
    });
  };

  return {
    getTriggerIntegration,
    getTriggerIntegrationTitle,
  };
};
